import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, Subscription, firstValueFrom, interval, map } from 'rxjs';
import { INotificationFeed } from '@curbnturf/entities';
import { NotificationFeedApiService } from '../backend-api/notification-feed-api.service';
import { AuthService } from '../auth/auth.service';

const POLL_MS = 60_000;
const FEED_LIMIT = 25;

type NotificationKind =
  | 'message_new'
  | 'booking_approved'
  | 'booking_declined'
  | 'booking_cancelled'
  | 'booking_request'          // host: new pending request on their listing
  | 'review_received'          // host: a guest left a review on their listing
  | 'review_eligible'          // guest: completed trip that hasn't been reviewed
  | 'trip_reminder'            // guest: trip starts tomorrow
  | 'guest_arriving_tomorrow'; // host: a guest arrives tomorrow

export interface INotification {
  id: string;
  kind: NotificationKind;
  icon: string;              // material-symbols name
  tone: 'trinidad' | 'jungle' | 'gold' | 'muted';
  title: string;
  subtitle: string;
  /** ISO timestamp the underlying event fired. */
  timestamp: string;
  routerLink: string;
  read: boolean;
}

const KIND_META: Record<NotificationKind, { icon: string; tone: INotification['tone'] }> = {
  message_new: { icon: 'forum', tone: 'trinidad' },
  booking_approved: { icon: 'check_circle', tone: 'jungle' },
  booking_declined: { icon: 'block', tone: 'muted' },
  booking_cancelled: { icon: 'event_busy', tone: 'muted' },
  booking_request: { icon: 'inbox', tone: 'trinidad' },
  review_received: { icon: 'star', tone: 'jungle' },
  review_eligible: { icon: 'redeem', tone: 'gold' },
  trip_reminder: { icon: 'luggage', tone: 'gold' },
  guest_arriving_tomorrow: { icon: 'luggage', tone: 'trinidad' },
};

/**
 * Real, server-backed nav-bar notification feed — every entry comes from
 * `GET /notification-feed`, which is populated by backend triggers on
 * message/booking/review/arrival events (see `NotificationFeedService` on
 * the server). Read state is server-authoritative (cross-device), mirroring
 * `MessageService`'s poll-refresh pattern in this same lib.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private platformId = inject<object>(PLATFORM_ID);
  private auth = inject(AuthService);
  private real = inject(NotificationFeedApiService);

  private readonly _notifications$ = new BehaviorSubject<INotification[]>([]);
  readonly notifications$: Observable<INotification[]> = this._notifications$.asObservable();
  readonly unreadCount$: Observable<number> = this.notifications$.pipe(
    map(list => list.filter(n => !n.read).length),
  );
  private pollSub: Subscription | null = null;

  constructor() {
    this.auth.currentUser$.subscribe(user => {
      if (user?.id) {
        void this.refreshNow();
        this.startPolling();
      } else {
        this.stopPolling();
        this._notifications$.next([]);
      }
    });
  }

  private startPolling(): void {
    if (!isPlatformBrowser(this.platformId) || this.pollSub) return;
    this.pollSub = interval(POLL_MS).subscribe(() => void this.refreshNow());
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  async refreshNow(): Promise<void> {
    try {
      const items = await firstValueFrom(this.real.fetchAll());
      this._notifications$.next(
        items
          .map(item => this.toINotification(item))
          .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
          .slice(0, FEED_LIMIT),
      );
    } catch (err) {
      console.error('[notification] failed to refresh feed', err);
    }
  }

  /** Optimistically marks read locally, then persists server-side. */
  markRead(id: string): void {
    const numericId = Number(id);
    if (!Number.isFinite(numericId)) return;
    const current = this._notifications$.value;
    const target = current.find(n => n.id === id);
    if (!target || target.read) return;
    this._notifications$.next(current.map(n => (n.id === id ? { ...n, read: true } : n)));
    this.real.markRead(numericId).subscribe({
      error: err => console.error('[notification] failed to mark read', id, err),
    });
  }

  markAllRead(notifications: INotification[]): void {
    if (notifications.every(n => n.read)) return;
    const ids = new Set(notifications.map(n => n.id));
    this._notifications$.next(
      this._notifications$.value.map(n => (ids.has(n.id) ? { ...n, read: true } : n)),
    );
    this.real.markAllRead().subscribe({
      error: err => console.error('[notification] failed to mark all read', err),
    });
  }

  private toINotification(item: INotificationFeed): INotification {
    const kind = item.kind as NotificationKind;
    const meta = KIND_META[kind] ?? { icon: 'notifications', tone: 'muted' as const };
    return {
      id: String(item.id),
      kind,
      icon: meta.icon,
      tone: meta.tone,
      title: item.title,
      subtitle: item.subtitle ?? '',
      timestamp: new Date(item.createdAt as unknown as string).toISOString(),
      routerLink: item.routerLink ?? '/',
      read: !!item.read,
    };
  }
}
